import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import styles from './SpellingModes.module.css';
import AITutor from '../AITutor/AITutor';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const AnagramChallengeMode = () => {
  const { userProgress, loading: userLoading, error: userError, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const initialized = useRef(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [scrambledLetters, setScrambledLetters] = useState([]);
  const [completingSession, setCompletingSession] = useState(false);

  const navigate = useNavigate();
  const { state } = useLocation();
  const difficulty = state?.difficulty || userProgress?.modeLevels?.anagram_challenge || 1;

  useEffect(() => {
    if (!userLoading && userProgress && !initialized.current) {
      startPractice();
      initialized.current = true;
    }
  }, [userLoading, userProgress]);

  useEffect(() => {
    if (questions.length > 0) {
      initializeQuestion();
    }
  }, [currentQuestion, questions]);

  const initializeQuestion = () => {
    setQuestionStartTime(Date.now());
    setUserInput('');
    setShowExplanation(false);
    setFeedback(null);
    
    // Use the scrambled version from the backend
    if (questions[currentQuestion]?.content?.scrambledVersion) {
      setScrambledLetters(questions[currentQuestion].content.scrambledVersion.split(''));
    }
  };

  const startPractice = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        mode: 'anagram_challenge',
        difficulty: difficulty
      });

      const questionsData = await apiRequest(`/spelling/questions?${params}`);

      const sessionData = await apiRequest('/sessions/start', {
        method: 'POST',
        body: JSON.stringify({
          mode: 'anagram_challenge',
          difficulty: difficulty,
          questions: questionsData.map(q => q._id)
        })
      });

      setQuestions(questionsData);
      localStorage.setItem('currentSessionId', sessionData._id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !userInput.trim()) return;
    setIsSubmitting(true);

    try {
      const sessionId = localStorage.getItem('currentSessionId');
      const currentQ = questions[currentQuestion];
      const timeSpent = (Date.now() - questionStartTime) / 1000;
      
      const result = await apiRequest(`/sessions/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify({
          questionId: currentQ._id,
          answer: userInput.trim(),
          timeSpent
        })
      });

      setFeedback({
        isCorrect: result.isCorrect,
        message: result.isCorrect ? 'Correct!' : 'Incorrect. Try again!'
      });
      setShowExplanation(true);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    setShowExplanation(false);
    
    if (currentQuestion + 1 >= questions.length) {
      try {
        setCompletingSession(true);
        const sessionId = localStorage.getItem('currentSessionId');
        
        const results = await apiRequest(`/sessions/${sessionId}/end`, {
          method: 'POST'
        });

        localStorage.removeItem('currentSessionId');
        updateProgress(results);
        
        // Navigate to results page with session results and questions
        navigate('/results', { 
          state: { 
            sessionResults: results,
            questions: questions
          } 
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setCompletingSession(false);
      }
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  if (loading || userLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className={styles.container}>
      {completingSession && (
        <div className={styles.overlay}>
          <Loader size="large" text="Analyzing your results..." />
        </div>
      )}
      <div className={styles.card}>
        <AITutor 
          currentQuestion={currentQ}
          userAnswer={userInput}
          isCorrect={feedback?.isCorrect}
        />
        
        <div className={styles.header}>
          <h2 className={styles.title}>Anagram Challenge</h2>
          <span className={styles.questionCount}>
            Question {currentQuestion + 1} of {questions.length}
          </span>
        </div>

        <h1 style={{marginBottom: '15px'}}><strong>Unscramble the letters to make the right word!</strong></h1>

        <div className={styles.progressContainer}>
          <div className={styles.progressBar} style={{ width: `${progress}%` }} />
        </div>


        <div className={styles.anagramContainer}>
          <div className={styles.scrambledLetters}>
            {scrambledLetters.map((letter, index) => (
              <span key={index} className={styles.letter}>
                {letter}
              </span>
            ))}
          </div>
          {currentQ?.content.hints?.map((hint, index) => (
            <div key={index} className={styles.hint}>{hint}</div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className={styles.inputForm}>
          <input
            type="text"
            id="answerInput"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className={styles.answerInput}
            placeholder="Unscramble the letters..."
            disabled={showExplanation}
          />
          {!showExplanation && (
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isSubmitting || !userInput.trim()}
            >
              Submit
            </button>
          )}
        </form>

        {feedback && (
          <div className={`${styles.feedback} ${feedback.isCorrect ? styles.correct : styles.incorrect}`}>
            {feedback.message}
          </div>
        )}

        {showExplanation && (
          <>
            <div className={styles.explanation}>
              <p>Correct word: {currentQ.content.correctAnswer}</p>
              <p>{currentQ.content.explanation}</p>
              <p>Definition: {currentQ.content.definition}</p>
              <p>Usage: {currentQ.content.usageExample}</p>
            </div>
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {currentQuestion + 1 >= questions.length ? 'Finish' : 'Next Word'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AnagramChallengeMode; 